import { Component, EventEmitter, Input, Output } from '@angular/core';

import { EidosBaseComponent } from '@common/components/eidos-base.component';
import { IEidosIcon } from '@common/config/environment.interface';

export interface IButtonBadgeConfiguration {
  text?: string;
  icon: IEidosIcon | null,
  tooltipText?: string;
  tooltipPosition?: string;
}

export enum ButtonTypes {
  CONTAINED = 'contained',
  CONTAINED_SUCCESS = 'contained-success',
  CONTAINED_DANGER = 'contained-danger',
  CONTAINED_LIGHT = 'contained-light',
  CONTAINED_PRIMARY = 'contained-primary',
  OUTLINED = 'outlined',
}
export enum ButtonSizes {
  BIG = 'big',
  NORMAL = 'normal',
  BIG_COMPACT = 'bigcompact',
  SMALL = 'small',
}

const ButtonStyles = {
  [ButtonTypes.CONTAINED]: 'res-button-contained',
  [ButtonTypes.CONTAINED_SUCCESS]: 'res-button-contained-success',
  [ButtonTypes.CONTAINED_DANGER]: 'res-button-contained-danger',
  [ButtonTypes.CONTAINED_LIGHT]: 'res-button-contained-light',
  [ButtonTypes.CONTAINED_PRIMARY]: 'res-button-contained-primary',
  [ButtonTypes.OUTLINED]: 'res-button-outlined',
  [ButtonSizes.BIG]: 'res-button-big',
  [ButtonSizes.NORMAL]: 'res-button-normal',
  [ButtonSizes.BIG_COMPACT]: 'res-button-bigcompact',
  [ButtonSizes.SMALL]: 'res-button-small',
  badgeConfiguration: 'res-button-margin-badge'
}

@Component({
  selector: 'res-button',
  templateUrl: './res-button.component.html',
  styleUrls: ['./res-button.component.scss'],
})
export class ResButtonComponent extends EidosBaseComponent {
  private _iconConfiguration: IEidosIcon | null = null;
  private _cssClasses: Array<string> = [];
  private _tooltipTarget: any;
  private _stylingMode: ButtonTypes = ButtonTypes.CONTAINED;
  private _size: ButtonSizes = ButtonSizes.SMALL;
  /**
   * Button styling mode, 'contained' or 'outlined'
   *
   * @type {string}
   * @memberof ResButtonComponent
   */
  @Input() set stylingMode(val: string) {
    if(val) {this._stylingMode = <ButtonTypes>val;
    this.cssClassesUpdate();}
  }
  /**
   * Button size, 'small' or 'big'
   *
   * @type {string}
   * @memberof ResButtonComponent
   */
  @Input() set size(val: string) {
    if(val) {this._size = <ButtonSizes>val;
    this.cssClassesUpdate();}
  }
  /**
   * Icon position, 'left' or 'right'
   *
   * @type {string}
   * @memberof ResButtonComponent
   */
  @Input() iconPosition: string = 'left';
  /**
   * Badge configuration
   *
   * @type {IButtonBadgeConfiguration}
   * @memberof ResButtonComponent
   */
  @Input() badgeConfiguration: IButtonBadgeConfiguration | null = null;
  /**
   * Button text
   *
   * @type {string}
   * @memberof ResButtonComponent
   */
  @Input() text: string = '';
  /**
   * Button disabled
   *
   * @type {boolean}
   * @memberof ResButtonComponent
   */
  @Input() isDisabled: boolean = false;
  /**
   * Click event emitter
   *
   * @memberof ResButtonComponent
   */
  @Output() btnClick = new EventEmitter();
  /**
   * Icon configuration
   *
   * @type {(IEidosIcon | null)}
   * @memberof ResButtonComponent
   */
  @Input() set iconConfiguration(value: IEidosIcon | null) {
    this._iconConfiguration = value;
  }
  /**
   * Button visibility
   *
   * @type {string}
   * @memberof ResButtonComponent
   */
  @Input() visible: boolean = true;

  public tooltipDefaultVisible: boolean = false;

  get tooltipTarget(): any {
    return this._tooltipTarget;
  }

  get iconConfiguration(): IEidosIcon | null {
    return this._iconConfiguration;
  }

  get cssClasses(): Array<string> {
    return this._cssClasses;
  }

  get stylingMode(): ButtonTypes {
    return this._stylingMode;
  }
  get size(): ButtonSizes {
    return this._size;
  }

  constructor() {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.cssClassesUpdate();
  }

  private cssClassesUpdate() {
    const buttonStyle = ButtonStyles[this.stylingMode];
    const buttonSize = ButtonStyles[this.size];
    const badgeConfiguration = this.badgeConfiguration? ButtonStyles.badgeConfiguration : '';
    this._cssClasses = [buttonStyle, buttonSize, badgeConfiguration];
  }

  onMouseEnter(event: MouseEvent) {
    this._tooltipTarget = event.target;
    this.tooltipDefaultVisible = true;
  }

  onMouseLeave() {
    this.tooltipDefaultVisible = false;
  }
}
